import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Row, Col, Card, Statistic, Table, Typography, Space, Tag, Button, message } from 'antd';
import { 
  UserOutlined, 
  BookOutlined, 
  TrophyOutlined, 
  RiseOutlined,
  EyeOutlined,
  EditOutlined,
  SettingOutlined
} from '@ant-design/icons';
import AdminLayout from '@/components/Layouts/AdminLayout/AdminLayout';
import { adminStudentApi, adminTextbookApi, adminExamResultApi } from '@/services/api';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;

type ActivityStatus = 'success' | 'processing' | 'warning' | 'default';

interface DashboardActivity {
  key: string;
  user: string;
  action: string;
  target: string;
  time: string;
  status: ActivityStatus;
  examResultId?: number;
  gradingStatus?: string;
}

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();

  // 통계 데이터 상태
  const [totalStudents, setTotalStudents] = useState<number | null>(null);
  const [totalLectures, setTotalLectures] = useState<number | null>(null); // 교재 수 기반
  const [completionRate, setCompletionRate] = useState<number | null>(null); // 집계 API 필요
  const [averageScore, setAverageScore] = useState<number | null>(null); // 집계 API 필요

  // 최근 활동 상태
  const [activities, setActivities] = useState<DashboardActivity[]>([]);

  // 통계 카드 구성
  const stats = useMemo(() => ([
    {
      title: '전체 학생',
      value: totalStudents ?? '-',
      icon: <UserOutlined />,
      color: '#1890ff',
      change: '',
      criteria: '집계 기준: 전체 등록 학생 수',
    },
    {
      title: '전체 강의',
      value: totalLectures ?? '-',
      icon: <BookOutlined />,
      color: '#52c41a',
      change: '',
      criteria: '집계 기준: 전체 등록 교재 수',
    },
    {
      title: '완료율',
      value: completionRate ?? 0,
      icon: <TrophyOutlined />,
      color: '#faad14',
      change: '',
      suffix: '%',
      criteria: '집계 기준: 시험 결과 중 채점 완료 비율',
    },
    {
      title: '평균 점수',
      value: averageScore ?? 0,
      icon: <RiseOutlined />,
      color: '#722ed1',
      change: '',
      suffix: '점',
      criteria: '집계 기준: 채점 완료된 시험 평균 점수',
    },
  ]), [totalStudents, totalLectures, completionRate, averageScore]);

  useEffect(() => {
    // 병렬로 필요한 데이터 불러오기
    const fetchAll = async () => {
      try {
        const [studentRes, textbookRes, examResultRes] = await Promise.all([
          adminStudentApi.listAll({ page: 0, size: 1 }),
          adminTextbookApi.list({ page: 0, size: 1 }),
          adminExamResultApi.list({}),
        ]);

        // 전체 학생 수
        {
          const pageData = (studentRes?.data || studentRes);
          const total = pageData?.totalElements ?? pageData?.page?.totalElements ?? 0;
          setTotalStudents(typeof total === 'number' ? total : 0);
        }

        // 전체 강의(교재 수로 대체)
        {
          const pageData = (textbookRes?.data || textbookRes);
          const total = pageData?.totalElements ?? pageData?.page?.totalElements ?? 0;
          setTotalLectures(typeof total === 'number' ? total : 0);
        }

        // 최근 활동 + 집계 계산 (시험 결과 기반)
        {
          const listData = (examResultRes?.data || examResultRes);
          const items = Array.isArray(listData?.items) ? listData.items : [];
          const totalCount = items.length;
          const completedItems = items.filter((it: any) => String(it?.gradingStatus || '').toLowerCase() === 'completed');
          const completionRateValue = totalCount > 0 ? Math.round((completedItems.length / totalCount) * 100) : 0;
          setCompletionRate(completionRateValue);

          const { scoreSum, scoreCount } = completedItems.reduce((acc: { scoreSum: number; scoreCount: number }, it: any) => {
            const score = Number(it?.score);
            if (Number.isFinite(score)) {
              acc.scoreSum += score;
              acc.scoreCount += 1;
            }
            return acc;
          }, { scoreSum: 0, scoreCount: 0 });
          const averageScoreValue = scoreCount > 0 ? Math.round(scoreSum / scoreCount) : 0;
          setAverageScore(averageScoreValue);

          const mapped: DashboardActivity[] = items.slice(0, 10).map((it: any, idx: number) => {
            const gradingStatus = String(it?.gradingStatus || '').toLowerCase();
            const status: ActivityStatus =
              gradingStatus === 'completed'
                ? 'success'
                : gradingStatus === 'in_progress'
                ? 'processing'
                : gradingStatus === 'pending'
                ? 'default'
                : 'default';
            return {
              key: String(it?.id ?? idx + 1),
              user: it?.studentName || '-',
              action: '시험 결과',
              target: it?.examName || '-',
              time: it?.completionDate || '-',
              status,
              examResultId: typeof it?.id === 'number' ? it.id : undefined,
              gradingStatus,
            };
          });
          setActivities(mapped);
        }
      } catch (e) {
        // 실패 시 최소한의 기본값 유지
        setActivities([]);
      }
    };
    fetchAll();
  }, []);

  const handleViewActivity = useCallback((record: DashboardActivity) => {
    if (!record.examResultId) {
      message.warning('연결된 시험 결과를 찾을 수 없습니다.');
      return;
    }
    navigate(`/admin/exam-result/detail/${record.examResultId}`);
  }, [navigate]);

  const handleEditActivity = useCallback((record: DashboardActivity) => {
    if (!record.examResultId) {
      message.warning('연결된 시험 결과를 찾을 수 없습니다.');
      return;
    }
    navigate(`/admin/exam-result/detail/${record.examResultId}`);
  }, [navigate]);

  const activityColumns = useMemo(() => [
    {
      title: '사용자',
      dataIndex: 'user',
      key: 'user',
    },
    {
      title: '활동',
      dataIndex: 'action',
      key: 'action',
    },
    {
      title: '대상',
      dataIndex: 'target',
      key: 'target',
    },
    {
      title: '시간',
      dataIndex: 'time',
      key: 'time',
    },
    {
      title: '상태',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusMap: Record<ActivityStatus, { color: string; text: string }> = {
          success: { color: 'green', text: '완료' },
          processing: { color: 'blue', text: '진행중' },
          warning: { color: 'orange', text: '주의' },
          default: { color: 'default', text: '일반' },
        };
        const { color, text } = statusMap[status as keyof typeof statusMap];
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: '액션',
      key: 'action',
      render: (_: unknown, record: DashboardActivity) => (
        <Space size="small">
          <Button type="text" icon={<EyeOutlined />} size="small" onClick={() => handleViewActivity(record)} />
          <Button type="text" icon={<EditOutlined />} size="small" onClick={() => handleEditActivity(record)} />
        </Space>
      ),
    },
  ], [handleEditActivity, handleViewActivity]);

  return (
    <AdminLayout>
      <div>
        <Title level={2}>관리자 대시보드</Title>
        <Text type="secondary">시스템 현황과 최근 활동을 확인하세요</Text>
        
        {/* 통계 카드 */}
        <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
          {stats.map((stat, index) => (
            <Col xs={24} sm={12} lg={6} key={index}>
              <Card>
                <Statistic
                  title={stat.title}
                  value={stat.value}
                  prefix={React.cloneElement(stat.icon, { style: { color: stat.color } })}
                  suffix={stat.suffix}
                  valueStyle={{ color: stat.color }}
                />
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary">
                    {stat.change ? (
                      <>
                    <RiseOutlined style={{ color: '#52c41a', marginRight: 4 }} />
                    {stat.change} 지난 달 대비
                      </>
                    ) : (
                      <span style={{ color: '#999' }}>{stat.criteria}</span>
                    )}
                  </Text>
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        {/* 최근 활동 */}
        <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
          <Col xs={24} lg={16}>
            <Card title="최근 활동" extra={<Button type="link" onClick={() => navigate('/admin/exam-result')}>더보기</Button>}>
              <Table
                columns={activityColumns}
                dataSource={activities}
                pagination={false}
                size="small"
              />
            </Card>
          </Col>
          
          <Col xs={24} lg={8}>
            <Card title="빠른 액션">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button type="primary" block icon={<UserOutlined />} onClick={() => navigate('/admin/student')}>
                  학생 관리
                </Button>
                <Button block icon={<BookOutlined />} onClick={() => navigate('/admin/learning/register')}>
                  학습등록
                </Button>
                <Button block icon={<TrophyOutlined />} onClick={() => navigate('/admin/exam-result')}>
                  성적표 관리
                </Button>
                <Button block icon={<SettingOutlined />} onClick={() => navigate('/admin/academy/list')}>
                  학원 관리
                </Button>
              </Space>
            </Card>
          </Col>
        </Row>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard; 